import { buildLabelsAndColors } from "../colorScale.js";
import { normalizeAreaIdForLevel } from "../idUtils.js";
import { isTrackingSchema } from "../schemaTypes.js";

/**
 * Computes the full sidebar aggregation payload: national/state/selected
 * bar charts, unfiltered baselines for demographic comparison, and wave-
 * series trend data.
 *
 * Verbatim lift of the MapPage class method.  All this.state / this.props /
 * method reads are surfaced as explicit fields on the single `inputs` object.
 * Config accessors (getActiveGroup, getGroups) are resolved by the wrapper
 * before calling.
 *
 * ⚠️  Raw-vs-filtered distinction: the tracking waveSeries section reads
 * inputs.metricRows and inputs.metricRowsStates directly (raw, all waves) —
 * the same fields that are also used as the BASE for the wave-filtered locals
 * rowsCurrent / rowsStates.  Do NOT collapse them.
 *
 * @param {object} inputs
 * @returns {{ national, state, selected, unfilteredNational, unfilteredState,
 *             unfilteredSelected, waveSeries, waveSeriesKey, waveSeriesLabel,
 *             waveSeriesKeys, waveSeriesKeyLabels, waveSeriesKeyColors }}
 */
export function getAggregatesForSidebar(inputs) {
    const {
      selectedFeature, selectedStateCode, metricRows, metricRowsStates,
      compareRowsLatest, ActionMode, theme_colours, compareWaves,
      waveSelect, waveFilter, preferenceFilter, ActionTargetSubVariables,
      group, groupsById,
    } = inputs;
    // geoLevel is read from inputs for parity with the original; it is
    // assigned but not used further inside the body (confirmed dead).
    const geoLevel = inputs.geoLevel; // eslint-disable-line no-unused-vars
    if (!group) return { national: [], state: [], selected: [], waveSeries: [] };

    const { labels, colors } = buildLabelsAndColors(groupsById, theme_colours);


    const aggregationCol = ((ActionMode === "filter" ? group?.TargetVariable1?.aggregation_column : group?.aggregation_column) || "total") + "_sum";



    const weightedAvg = (rows, key) => {
      let num = 0, den = 0;
      for (const r of rows || []) {
        const val = Number(r?.[key]);
        const w   = Number(r?.[aggregationCol]);
        if (Number.isFinite(val) && Number.isFinite(w)) {
          num += val * w;
          den += w;
        }
      }
      return den ? num / den : null;
    };

    const collapse = (rows) => {
      const keys = ActionMode === "filter"
        ? (group?.TargetVariable1?.keys || [])
        : (group?.keys || []);
      return keys.map((k) => ({
        key: k,
        label: labels[k] || k,
        value: weightedAvg(rows, k),
      }));
    };

    // The slice(-4) suffix-match in the selected-feature matchers below is valid
    // ONLY for 4-digit CD ids: normalizeAreaIdForLevel pads "cd" to 5 chars, so
    // slice(-4) recovers the canonical 4-digit GEOID. On genuinely 5-digit ids
    // (counties / zipcodes / statesenate / statehouse) slice(-4) drops the leading
    // STATE digit and collides across states (e.g. county 27123 ↔ 17123 ↔ 37123),
    // folding same-suffix areas into the selected bucket. So: cd → suffix allowed;
    // every other level → exact normalized-id match only.
    const allowSuffixMatch = (lvl) => lvl === "cd";

    // current level rows (counties/cd/zip/states)
    let rowsCurrentAll = Array.isArray(metricRows) ? metricRows : [];
    // baseline rows: one per state
    let rowsStatesAll  = Array.isArray(metricRowsStates) ? metricRowsStates : [];

    // In compare mode, use latest-wave rows for bars/table
    if (compareWaves && Array.isArray(compareRowsLatest) && compareRowsLatest.length) {
      rowsCurrentAll = compareRowsLatest;
      rowsStatesAll = [];
    }
    let rowsCurrent = rowsCurrentAll;
    let rowsStates  = rowsStatesAll;

    // If tracking schema, align sidebar with selected wave
    const activeSchema =
      ActionMode === "filter"
        ? group?.TargetVariable1?.schema
        : group?.schema;
    if (isTrackingSchema(activeSchema)) {
      const wave = waveSelect ?? waveFilter;
      if (wave != null) {
        const w = String(wave);
        rowsCurrent = rowsCurrent.filter(r => String(r?.wave) === w);
        rowsStates = rowsStates.filter(r => String(r?.wave) === w);
      }
    }

    // NATIONAL: average across states if we have baseline, else use current level
    let national = [];
    if (rowsStates.length) {
      national = collapse(rowsStates);
    } else if (rowsCurrent.length) {
      national = collapse(rowsCurrent);
    }

    // STATE: prefer fast single-state filtered fetch (metricRowsState) so the state bar
    // appears immediately without waiting for the full all-states baseline.
    // Fall back to metricRowsStates (filtered by code) or metricRows as before.
    let state = [];
    if (selectedStateCode) {
      const sc = String(selectedStateCode).padStart(2, "0");
      let stateFilteredRows = Array.isArray(inputs.metricRowsState) ? inputs.metricRowsState : [];
      // Safety net: if rows have a wave column, align with the selected wave so
      // metricRowsState (fast fetch) doesn't show cross-wave averages.
      if (stateFilteredRows.length && isTrackingSchema(activeSchema)) {
        const wv = waveSelect ?? waveFilter;
        if (wv != null) {
          const wStr = String(wv);
          const waveFiltered = stateFilteredRows.filter(r => String(r?.wave) === wStr);
          if (waveFiltered.length) stateFilteredRows = waveFiltered;
        }
      }
      if (stateFilteredRows.length) {
        const rows = stateFilteredRows.filter(r => String(r?.area_id).padStart(2, "0") === sc);
        if (rows.length) state = collapse(rows);
      }
      if (!state.length) {
        if (rowsStates.length) {
          state = collapse(rowsStates.filter(r => String(r?.area_id).padStart(2, "0") === sc));
        } else if (rowsCurrent.length) {
          state = collapse(rowsCurrent.filter(r => {
            const sf = r?.state_fips ?? r?.state ?? r?.STATEFP ?? r?.area_id ?? "";
            return String(sf).padStart(2, "0") === sc;
          }));
        }
      }
    }

    // SELECTED: current level feature (county/cd/zip/state) from current rows
    let selected = [];
    if (selectedFeature && rowsCurrent.length) {
      const lvl  = selectedFeature.level;
      const norm = (id) => normalizeAreaIdForLevel(lvl, id);
      const useSuffix = allowSuffixMatch(lvl);
      const ids = new Set(
        (selectedFeature.rowMatchIds || [selectedFeature.value])
          .map((id) => String(id))
          .flatMap((id) => {
            const n = norm(id);
            return (useSuffix ? [id, n, n?.slice?.(-4)] : [id, n]).filter(Boolean);
          })
      );
      selected = collapse(rowsCurrent.filter((r) => {
        const id = String(r?.area_id ?? "");
        return ids.has(id) || ids.has(norm(id)) || (useSuffix && ids.has(norm(id)?.slice?.(-4)));
      }));
    }

    // ---- Wave series for sidebar trend chart ----
    const waveSeries = [];
    let waveSeriesKey = null;
    let waveSeriesLabel = null;
    const hasSubareaSelection = Boolean(selectedFeature && selectedFeature.level !== "states");

    // Determine which key to track (works for any schema)
    const pref = preferenceFilter;
    const isExploreWave = ActionMode === "explore";
    const isLikert = isExploreWave && group?.type === "likert" && group?.polarity?.pos && group?.polarity?.neg;
    if (isLikert && (!pref || pref === "net")) {
      waveSeriesKey = "net";
    } else if (isExploreWave && pref && group?.keys?.includes(pref)) {
      waveSeriesKey = pref;
    } else if (isExploreWave && group?.keys?.length) {
      waveSeriesKey = group.keys[0];
    } else if (!isExploreWave) {
      waveSeriesKey = ActionTargetSubVariables?.TargetSubVariable1 || group?.TargetVariable1?.keys?.[0];
    }
    waveSeriesLabel = labels[waveSeriesKey] || waveSeriesKey;

    const toArr = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
    const valueForRow = (row) => {
      if (!row || !waveSeriesKey) return null;
      if (waveSeriesKey === "net") {
        const posKeys = toArr(isExploreWave ? group?.polarity?.pos : group?.TargetVariable1?.polarity?.pos);
        const negKeys = toArr(isExploreWave ? group?.polarity?.neg : group?.TargetVariable1?.polarity?.neg);
        const pos = posKeys.reduce((acc, k) => acc + (Number(row?.[k]) || 0), 0);
        const neg = negKeys.reduce((acc, k) => acc + (Number(row?.[k]) || 0), 0);
        return Number.isFinite(pos) && Number.isFinite(neg) ? pos - neg : null;
      }
      const v = Number(row?.[waveSeriesKey]);
      return Number.isFinite(v) ? v : null;
    };

    const groupByWave = (rows) => {
      const acc = new Map();
      rows.forEach((r) => {
        const w = r?.wave;
        if (w == null) return;
        const key = String(w);
        const val = valueForRow(r);
        const wt = Number(r?.[aggregationCol]);
        if (!Number.isFinite(val) || !Number.isFinite(wt)) return;
        const curr = acc.get(key) || { num: 0, den: 0 };
        curr.num += val * wt;
        curr.den += wt;
        acc.set(key, curr);
      });
      return acc;
    };

    // rowsLatest/rowsPrev have no wave field — each set IS one wave, fetched separately.
    // Compute weighted averages directly from each set; do NOT group by r.wave.
    const avgRows = (rows) => {
      let num = 0, den = 0;
      for (const r of rows || []) {
        const val = valueForRow(r);
        const wt = Number(r?.[aggregationCol]);
        if (Number.isFinite(val) && Number.isFinite(wt)) { num += val * wt; den += wt; }
      }
      return den ? num / den : null;
    };
    const filterByState = (rows) => {
      const sc = selectedStateCode != null ? String(selectedStateCode).padStart(2, "0") : null;
      if (!sc) return [];
      return rows.filter((r) => {
        const sf = r?.state_fips ?? r?.state ?? r?.STATEFP ?? r?.area_id ?? "";
        return String(sf).padStart(2, "0") === sc;
      });
    };
    const filterBySelected = (rows) => {
      if (!selectedFeature) return [];
      const lvl = selectedFeature.level;
      const norm = (id) => normalizeAreaIdForLevel(lvl, id);
      const useSuffix = allowSuffixMatch(lvl);
      const rowIds = new Set((selectedFeature.rowMatchIds || []).map((id) => String(id)));
      return rows.filter((r) => {
        const id = String(r?.area_id ?? "");
        return rowIds.has(id) || rowIds.has(norm(id)) || (useSuffix && rowIds.has(norm(id)?.slice?.(-4)));
      });
    };
    const buildSeriesFromPair = (rowsLatest, rowsPrev, latestWave, prevWave) => {
      [[prevWave, rowsPrev], [latestWave, rowsLatest]].forEach(([w, rows]) => {
        waveSeries.push({
          wave: Number(w),
          national: avgRows(rows),
          state: avgRows(filterByState(rows)),
          selected: avgRows(filterBySelected(rows)),
        });
      });
    };

    if (isTrackingSchema(activeSchema)) {
      // Tracking schema: always build from raw all-wave rows so all waves show in the trend chart
      // ⚠️  These deliberately re-read inputs.metricRowsStates and inputs.metricRows (raw, all
      // waves), NOT the wave-filtered locals rowsStates / rowsCurrent.
      const rawNatRows = Array.isArray(inputs.metricRowsStates) && inputs.metricRowsStates.length
        ? inputs.metricRowsStates
        : Array.isArray(inputs.metricRows) ? inputs.metricRows : [];
      const rawAllRows = Array.isArray(inputs.metricRows) ? inputs.metricRows : [];

      const waveMapNational = groupByWave(rawNatRows);
      const waveMapState = selectedStateCode
        ? groupByWave(rawNatRows.filter(r => String(r?.area_id ?? "").padStart(2, "0") === String(selectedStateCode).padStart(2, "0")))
        : new Map();

      let waveMapSelected = new Map();
      if (hasSubareaSelection && rawAllRows.length) {
        const lvl = selectedFeature.level;
        const norm = (id) => normalizeAreaIdForLevel(lvl, id);
        const useSuffix = allowSuffixMatch(lvl);
        const rowIds = new Set((selectedFeature.rowMatchIds || []).map((id) => String(id)));
        waveMapSelected = groupByWave(rawAllRows.filter((r) => {
          const id = String(r?.area_id ?? "");
          return rowIds.has(id) || rowIds.has(norm(id)) || (useSuffix && rowIds.has(norm(id).slice(-4)));
        }));
      } else if (selectedStateCode) {
        waveMapSelected = waveMapState;
      }

      const waves = Array.from(new Set([
        ...Array.from(waveMapNational.keys()),
        ...Array.from(waveMapState.keys()),
        ...Array.from(waveMapSelected.keys()),
      ])).sort((a, b) => Number(a) - Number(b));

      waves.forEach((w) => {
        const getAvg = (m) => {
          const rec = m.get(w);
          return rec && rec.den ? rec.num / rec.den : null;
        };
        waveSeries.push({
          wave: Number(w),
          national: getAvg(waveMapNational),
          state: getAvg(waveMapState),
          selected: getAvg(waveMapSelected),
        });
      });
    } else if (compareWaves && Array.isArray(inputs.compareRowsLatest) && Array.isArray(inputs.compareRowsPrev)) {
      // Non-tracking variable in compare mode: 2-point series from the two fetched waves
      const latestWave = inputs.compareWavePair?.[0];
      const prevWave = inputs.compareWavePair?.[1];
      if (latestWave != null && prevWave != null) {
        buildSeriesFromPair(inputs.compareRowsLatest, inputs.compareRowsPrev, latestWave, prevWave);
      }
    }
    waveSeries.sort((a, b) => Number(a.wave) - Number(b.wave));

    // ---- Multi-key wave series (per-category trend lines for sidebar) ----
    let waveSeriesKeys = null;
    let waveSeriesKeyLabels = null;
    let waveSeriesKeyColors = null;
    const isExploreMode = ActionMode === "explore";
    if (isTrackingSchema(activeSchema) && isExploreMode && (group?.keys?.length > 1 || group?.polarity)) {
      const groupKeys = group.keys || [];
      waveSeriesKeys = groupKeys;
      waveSeriesKeyLabels = Object.fromEntries(groupKeys.map(k => [k, labels[k] || k]));
      waveSeriesKeyColors = Object.fromEntries(groupKeys.map(k => [k, colors[k] || "#2563eb"]));

      // Always use raw all-wave metricRows so the full trend is visible regardless of compare mode
      // ⚠️  Raw read — same pattern as tracking waveSeries above.
      const rawCurrentRows = Array.isArray(inputs.metricRows) ? inputs.metricRows : [];
      const rawStateRows2 = Array.isArray(inputs.metricRowsStates) ? inputs.metricRowsStates : [];
      const allNatRows = rawStateRows2.length ? rawStateRows2 : rawCurrentRows;

      const groupByWaveMulti = (rows) => {
        const waveMap = new Map();
        rows.forEach(r => {
          const w = r?.wave;
          if (w == null) return;
          const wKey = String(w);
          const wt = Number(r?.[aggregationCol]);
          if (!Number.isFinite(wt) || wt <= 0) return;
          const curr = waveMap.get(wKey) || { wt: 0, vals: {} };
          groupKeys.forEach(k => {
            const v = Number(r?.[k]);
            if (Number.isFinite(v)) curr.vals[k] = (curr.vals[k] || 0) + v * wt;
          });
          curr.wt += wt;
          waveMap.set(wKey, curr);
        });
        return waveMap;
      };

      const waveMapNatMulti = groupByWaveMulti(allNatRows);
      const waveMapStateMulti = selectedStateCode
        ? groupByWaveMulti(
            allNatRows.filter(
              (r) => String(r?.area_id ?? "").padStart(2, "0") === String(selectedStateCode).padStart(2, "0")
            )
          )
        : new Map();
      let selRowsAll = [];
      if (hasSubareaSelection) {
        const lvl = selectedFeature.level;
        const norm = (id) => normalizeAreaIdForLevel(lvl, id);
        const useSuffix = allowSuffixMatch(lvl);
        const rowIds = new Set((selectedFeature.rowMatchIds || [selectedFeature.value || ""]).map(id => String(id)));
        selRowsAll = rawCurrentRows.filter(r => {
          const id = String(r?.area_id ?? "");
          return rowIds.has(id) || rowIds.has(norm(id)) || (useSuffix && rowIds.has(norm(id)?.slice?.(-4)));
        });
      }
      const waveMapSelMulti = groupByWaveMulti(selRowsAll);

      const allWavesMulti = Array.from(new Set([
        ...Array.from(waveMapNatMulti.keys()),
        ...Array.from(waveMapSelMulti.keys()),
      ])).sort((a, b) => Number(a) - Number(b));

      const hasExplicitSelection = hasSubareaSelection;
      allWavesMulti.forEach(w => {
        let row = waveSeries.find(r => String(r.wave) === w);
        if (!row) { row = { wave: Number(w) }; waveSeries.push(row); }
        const natRec = waveMapNatMulti.get(w);
        const stateRec = waveMapStateMulti.get(w);
        const selRec = waveMapSelMulti.get(w);
        groupKeys.forEach(k => {
          const natVal = natRec?.wt ? (natRec.vals[k] != null ? natRec.vals[k] / natRec.wt : null) : null;
          const stateVal = stateRec?.wt ? (stateRec.vals[k] != null ? stateRec.vals[k] / stateRec.wt : null) : null;
          const selVal = selRec?.wt ? (selRec.vals[k] != null ? selRec.vals[k] / selRec.wt : null) : null;
          row[k + "__nat"] = natVal;
          row[k + "__state"] = stateVal;
          row[k + "__selected"] = hasExplicitSelection ? selVal : (stateVal != null ? stateVal : natVal);
          row[k] = hasExplicitSelection ? selVal : (stateVal != null ? stateVal : natVal);
        });
      });
      waveSeries.sort((a, b) => Number(a.wave) - Number(b.wave));
    }

    // ── Unfiltered baselines for demographic comparison ────────────────────────
    // These power the isDemoCompareMode chart view in Sidebar when a demo filter
    // is active — showing the filtered group alongside the overall area.
    const rawUnfNat  = Array.isArray(inputs.metricRowsStatesUnfiltered) ? inputs.metricRowsStatesUnfiltered : [];
    const rawUnfCurr = Array.isArray(inputs.metricRowsUnfiltered) ? inputs.metricRowsUnfiltered : [];

    // Apply wave filter for tracking schema
    let unfNatRows  = rawUnfNat;
    let unfCurrRows = rawUnfCurr;
    if (isTrackingSchema(activeSchema)) {
      const wv = waveSelect ?? waveFilter;
      if (wv != null) {
        const wStr = String(wv);
        unfNatRows  = unfNatRows.filter(r => String(r?.wave) === wStr);
        unfCurrRows = unfCurrRows.filter(r => String(r?.wave) === wStr);
      }
    }

    const unfilteredNational = unfNatRows.length ? collapse(unfNatRows) : [];

    const unfilteredState = selectedStateCode && unfNatRows.length
      ? collapse(unfNatRows.filter(r => String(r?.area_id ?? "").padStart(2, "0") === String(selectedStateCode).padStart(2, "0")))
      : [];

    const unfilteredSelected = (() => {
      if (!selectedFeature || selectedFeature.level === "states" || !unfCurrRows.length) return [];
      const lvl = selectedFeature.level;
      const norm = (id) => normalizeAreaIdForLevel(lvl, id);
      const useSuffix = allowSuffixMatch(lvl);
      const ids = new Set(
        (selectedFeature.rowMatchIds || [selectedFeature.value])
          .map(id => String(id))
          .flatMap(id => {
            const n = norm(id);
            return (useSuffix ? [id, n, n?.slice?.(-4)] : [id, n]).filter(Boolean);
          })
      );
      const rows = unfCurrRows.filter(r => {
        const id = String(r?.area_id ?? "");
        return ids.has(id) || ids.has(norm(id)) || (useSuffix && ids.has(norm(id)?.slice?.(-4)));
      });
      return rows.length ? collapse(rows) : [];
    })();

    return { national, state, selected, unfilteredNational, unfilteredState, unfilteredSelected, waveSeries, waveSeriesKey, waveSeriesLabel, waveSeriesKeys, waveSeriesKeyLabels, waveSeriesKeyColors };
}
